import { Flex } from "../primitives/Flex";
import { ChevronDown } from "@acto/web-common/components";
import { styled } from "@pigment-css/react";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";

const NavigationBarContainer = styled("div")(({ theme }) => ({
  width: "256px",
  height: "100vh",
  backgroundColor: theme.managementView.colors.neutral[0],
  borderRight: `1px solid ${theme.managementView.colors.neutral[8]}`,
  display: "flex",
  flexDirection: "column",
  padding: theme.managementView.spacing[12],
}));

const CompanyDropdown = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[12],
  padding: `${theme.managementView.spacing[12]} ${theme.managementView.spacing[18]}`,
  backgroundColor: "transparent",
  border: "none",
  borderRadius: theme.managementView.radius[16],
  cursor: "pointer",
  width: "100%",
  marginBottom: theme.managementView.spacing[16],
  transition: "background-color 0.2s",

  "& svg": {
    height: "12px",
    width: "12px",
  },

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

const CompanyIconWrapper = styled("div")({
  width: "16px",
  height: "16px",
  flexShrink: 0,
  "& svg": {
    width: "100%",
    height: "100%",
  },
});

const CompanyIconFallback = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: theme.colors.brand[500],
  borderRadius: theme.radius[5],
}));

const CompanyName = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  lineHeight: theme.managementView.font.lineHeight[120],
  letterSpacing: theme.managementView.font.letterSpacing.normal,
  color: theme.managementView.colors.neutral[90],
  margin: 0,
}));

const NavItem = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[12]}`,
  backgroundColor: "transparent",
  border: "none",
  borderRadius: theme.managementView.radius[16],
  cursor: "pointer",
  width: "100%",
  textAlign: "left",
  marginBottom: theme.managementView.spacing[2],
  transition: "background-color 0.2s",

  "& p": {
    fontFamily: theme.managementView.font.family,
    fontWeight: theme.managementView.font.weight[500],
    fontSize: theme.managementView.font.size[17],
    lineHeight: theme.managementView.font.lineHeight[120],
    letterSpacing: theme.managementView.font.letterSpacing.tight,
    color: theme.managementView.colors.neutral[56],
  },
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },

  '&[data-active="true"]': {
    backgroundColor: theme.managementView.colors.neutral[4],
    "& p": {
      color: theme.managementView.colors.neutral[100],
    },
  },
}));

const DropdownContent = styled(RadixDropdown.Content)(({ theme }) => ({
  minWidth: "160px",
  backgroundColor: theme.managementView.colors.neutral[0],
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[16],
  // Dropdown was not defined in Figma, so we used the default styles from the widget dropdowns
  boxShadow: theme.shadows[2],
  padding: theme.managementView.spacing[8],
  zIndex: 10000,
}));

const DropdownMenuItem = styled(RadixDropdown.Item)(({ theme }) => ({
  color: theme.managementView.colors.neutral[100],
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[18]}`,
  borderRadius: theme.managementView.radius[16],
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  fontSize: theme.managementView.font.size[17],
  fontWeight: theme.managementView.font.weight[500],
  fontFamily: theme.managementView.font.family,
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  backgroundColor: "transparent",
  border: "none",
  outline: "none",
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
  "&:focus": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

export interface NavigationItem {
  label: string;
  route: string;
}

export interface DropdownItem {
  label: string;
  onSelect: () => void;
}

export interface ManagementNavigationBarProps {
  companyName: string;
  companyIcon?: React.ReactNode;
  currentRoute: string;
  navigationItems: NavigationItem[];
  dropdownItems: DropdownItem[];
  onNavigate: (route: string) => void;
}

export const ManagementNavigationBar = ({
  companyName,
  companyIcon,
  currentRoute,
  navigationItems,
  dropdownItems,
  onNavigate,
}: ManagementNavigationBarProps) => {
  return (
    <NavigationBarContainer>
      <RadixDropdown.Root modal={false}>
        <RadixDropdown.Trigger asChild>
          <CompanyDropdown>
            <CompanyIconWrapper>{companyIcon ?? <CompanyIconFallback />}</CompanyIconWrapper>
            <CompanyName>{companyName}</CompanyName>
            <ChevronDown />
          </CompanyDropdown>
        </RadixDropdown.Trigger>
        <RadixDropdown.Portal>
          <DropdownContent align="start" sideOffset={4}>
            {dropdownItems.map((item, index) => (
              <DropdownMenuItem key={index} onSelect={item.onSelect}>
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownContent>
        </RadixDropdown.Portal>
      </RadixDropdown.Root>

      <Flex direction="column" gap={8}>
        {navigationItems.map((item) => (
          <NavItem key={item.route} data-active={currentRoute === item.route} onClick={() => onNavigate(item.route)}>
            <p>{item.label}</p>
          </NavItem>
        ))}
      </Flex>
    </NavigationBarContainer>
  );
};
